'use client';

import './FeaturesCard.css';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, useState } from 'react';
import Link from 'next/link';
import React from 'react';
import featuresCardData from '@/textContent/index/featuresCardData.json';

export default function FeaturesCard() {
    const keys = Object.keys(featuresCardData);
    const [activeTab, setActiveTab] = useState(keys[0]);

    // Animation variants
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: 'easeOut' },
        },
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
            },
        },
    };

    const tabVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.4, ease: 'easeOut' },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: 'easeOut' },
        },
        exit: {
            opacity: 0,
            y: -30,
            transition: { duration: 0.3 },
        },
    };

    const listItemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.4 },
        },
    };

    return (
        <Fragment>
            <div
                id="main-delivery"
                className="w-full min-h-screen bg-white py-1 px-0 md:px-18 relative"
                style={{
                    backgroundImage: 'url("/images/bg-2.png")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center top',
                    backgroundSize: 'cover',
                }}
            >
                <div className="absolute inset-0  pointer-events-none"></div>

                <div className="relative z-10">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        variants={staggerContainer}
                        id="upper"
                        className="w-full text-center py-5"
                    >
                        <motion.p
                            variants={fadeInUp}
                            className="text-3xl md:text-5xl font-extrabold text-[#2e2e2e]"
                        >
                            ERP in Various Industries <br />
                            <span className="gradient-text">
                                Tailored Solutions for Every Sector
                            </span>
                        </motion.p>
                        <motion.p
                            variants={fadeInUp}
                            className="text-sm md:text-md mt-3 font-semibold"
                        >
                            We understand that ERP is not a one-size-fits-all
                            solution—each industry <br /> comes with its unique
                            challenges and requirements.
                        </motion.p>

                        <br />
                        <motion.p
                            variants={fadeInUp}
                            className="text-sm md:text-base mt-4 max-w-3xl mx-auto leading-relaxed"
                        >
                            whether you operate in retail, healthcare,
                            manufacturing, real estate, distribution,
                            agriculture, or beyond. With Wahni IT Solutions, the
                            go-to ERPnext solution in Ernakulam, allows you to
                            optimize inventory, streamline production, and
                            quickly and effectively react to changing market
                            demands. As a leading provider of ERPnext in Kerala,
                            we empower businesses to achieve operational
                            excellence and drive growth through seamless
                            integration and innovative solutions. You can
                            streamline production, optimize inventory
                            management, and respond swiftly to evolving market
                            demands with confidence and ease.
                        </motion.p>
                    </motion.div>
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                        variants={staggerContainer}
                        id=""
                        className="lower my-10 flex flex-col items-center"
                    >
                        <motion.div
                            variants={staggerContainer}
                            className="flex gap-6 justify-center items-start p-5 overflow-x-auto w-full"
                        >
                            {keys.map((tab, index) => (
                                <motion.div
                                    key={index}
                                    variants={tabVariants}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className={`${
                                        activeTab === tab
                                            ? 'text-black font-semibold p-8 border border-gray-300 rounded-lg shadow-lg shadow-blue-50 transition duration-300 bg-white'
                                            : 'font-medium text-gray-700 border shadow-sm p-8 rounded-lg transition duration-300 scale-100 hover:scale-80 bg-white/80'
                                    } cursor-pointer`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    <div className="text-center">
                                        <i
                                            className={`ri-${featuresCardData[tab].icon} text-3xl`}
                                        ></i>
                                    </div>
                                    <p>{tab}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                id=""
                                className="card border rounded-lg w-full md:w-4/5 py-8 px-5 md:px-10 grid md:grid-cols-2 gap-5 shadow-lg shadow-blue-50 mt-1 bg-white"
                            >
                                <motion.div
                                    initial="hidden"
                                    animate="visible"
                                    variants={staggerContainer}
                                >
                                    <motion.h2
                                        variants={fadeInUp}
                                        className="font-bold text-xl md:text-2xl my-3"
                                    >
                                        {featuresCardData[activeTab].heading}
                                    </motion.h2>
                                    <motion.p
                                        variants={fadeInUp}
                                        className="text-base md:text-lg font-semibold mt-4"
                                    >
                                        {featuresCardData[activeTab].caption}
                                    </motion.p>
                                    <motion.ul
                                        variants={staggerContainer}
                                        className="text-sm md:text-md mt-4"
                                    >
                                        {featuresCardData[
                                            activeTab
                                        ].benefits.map((benefit, idx) => (
                                            <motion.li
                                                key={idx}
                                                variants={listItemVariants}
                                            >
                                                <i className="ri-checkbox-circle-line text-green-700"></i>
                                                &nbsp;
                                                <span className="font-normal text-sm md:text-lg">
                                                    {benefit}
                                                </span>
                                            </motion.li>
                                        ))}
                                    </motion.ul>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: 30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="w-full md:w-3/4 mx-auto md:ms-auto"
                                >
                                    <motion.ul
                                        variants={staggerContainer}
                                        initial="hidden"
                                        animate="visible"
                                        className="text-sm font-semibold border-2 border-gray-300 rounded-lg bg-white"
                                    >
                                        {featuresCardData[activeTab].points.map(
                                            (item, idx) => (
                                                <motion.li
                                                    key={item.title}
                                                    variants={listItemVariants}
                                                    className={
                                                        idx !==
                                                        featuresCardData[
                                                            activeTab
                                                        ].points.length -
                                                            1
                                                            ? `border-b-2 border-gray-300 p-3`
                                                            : 'p-3'
                                                    }
                                                >
                                                    <i
                                                        className={`ri-${item.icon} text-base md:text-lg mx-2 text-gray-600`}
                                                    ></i>
                                                    <span className="text-base md:text-lg font-medium">
                                                        {item.title}
                                                    </span>
                                                </motion.li>
                                            ),
                                        )}
                                    </motion.ul>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.5,
                                            delay: 0.4,
                                        }}
                                        className="mx-auto md:ms-auto"
                                    >
                                        <Link
                                            rel="canonical"
                                            href="/contact-sales"
                                        >
                                            <motion.button
                                                whileHover={{ scale: 1.03 }}
                                                whileTap={{ scale: 0.97 }}
                                                className="mt-6 w-full bg-gradient-to-r from-[#4695F0] to-[#FC42CE] py-3 md:py-2 text-sm md:text-md rounded-md font-bold text-white shadow-md hover:shadow-lg focus:outline-none focus:ring focus:border-blue-300"
                                            >
                                                Use this Solution{' '}
                                                <i className="ri-arrow-right-line"></i>
                                            </motion.button>
                                        </Link>
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>
                </div>
            </div>
        </Fragment>
    );
}
